import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Package, Plus, CheckCircle2, Clock } from "lucide-react";
import { useMaintenance } from "@/components/MaintenanceLayout";
import MachineIdentity from "@/components/maintenance/MachineMark";
import { agoFrom, formatINR } from "@/components/maintenance/lib";

// Spare orders — what maintenance is waiting for, what it cost, and which
// breakdown it unblocks. Orders never disappear: they change status
// (ordered → backordered → received) and stay on the record.

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLE = {
  ordered:     { label: "Ordered",     cls: "rl-tx-amber", border: "rgba(146,96,10,0.35)",  bg: "rgba(146,96,10,0.08)" },
  backordered: { label: "Backordered", cls: "rl-tx-amber", border: "rgba(146,96,10,0.45)",  bg: "rgba(146,96,10,0.12)" },
  received:    { label: "Received",    cls: "rl-tx-green", border: "rgba(21,128,61,0.35)",  bg: "rgba(21,128,61,0.08)" },
};

const Th = ({ children, right, width }) => (
  <th
    className={`px-4 py-2.5 text-[9.5px] uppercase tracking-[0.09em] font-semibold text-[hsl(var(--muted-foreground))] ${right ? "text-right" : "text-left"}`}
    style={width ? { width } : undefined}
  >
    {children}
  </th>
);
const Td = ({ children, right, className = "" }) => (
  <td className={`px-4 py-[13px] border-t border-[hsl(var(--border))]/60 align-middle ${right ? "text-right" : ""} ${className}`}>{children}</td>
);

function StatusChip({ status }) {
  const st = STATUS_STYLE[status] || STATUS_STYLE.ordered;
  return (
    <span className={`text-[10px] font-bold uppercase tracking-[0.05em] px-2 py-[3px] rounded-full border ${st.cls}`}
          style={{ borderColor: st.border, background: st.bg }}>
      {st.label}
    </span>
  );
}

// Log an order that isn't tied to a paused repair — the same three fields the
// pause dialog reveals, kept in one quiet row.
function AddRow({ machines, onAdded }) {
  const [open, setOpen] = useState(false);
  const [part, setPart] = useState("");
  const [qty, setQty] = useState("1");
  const [price, setPrice] = useState("");
  const [machineNo, setMachineNo] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!part.trim() || busy) return;
    setBusy(true);
    try {
      await axios.post(`${API}/maintenance/spares`, {
        part_name: part.trim(),
        qty: Number(qty) || 1,
        price_inr: price.trim() === "" ? null : Number(price),
        machine_no: machineNo || null,
      });
      toast.success(`${part.trim()} logged as ordered`);
      setPart(""); setQty("1"); setPrice(""); setMachineNo(""); setOpen(false);
      onAdded();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not log that order.");
    } finally { setBusy(false); }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        data-testid="mt-spares-add"
        className="rl-lit-btn inline-flex items-center gap-1.5 h-8 px-3.5 rounded-full bg-[hsl(var(--primary))] text-white text-[12px] font-bold"
      >
        <Plus className="w-3.5 h-3.5" /> Log a spare order
      </button>
    );
  }
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="mt-spares-add-row">
      <input
        value={part} onChange={(e) => setPart(e.target.value)} autoFocus
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submit(); } if (e.key === "Escape") setOpen(false); }}
        placeholder="Part ordered…" data-testid="mt-spares-part"
        className="h-8 w-[200px] rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 text-[12.5px] outline-none focus:border-[hsl(var(--primary))]/60"
      />
      <input
        value={qty} onChange={(e) => setQty(e.target.value)} inputMode="decimal" placeholder="Qty"
        data-testid="mt-spares-qty"
        className="h-8 w-[64px] rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 text-[12.5px] font-mono-data text-right outline-none focus:border-[hsl(var(--primary))]/60"
      />
      <input
        value={price} onChange={(e) => setPrice(e.target.value)} inputMode="decimal" placeholder="₹ (optional)"
        data-testid="mt-spares-price"
        className="h-8 w-[110px] rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 text-[12.5px] font-mono-data text-right outline-none focus:border-[hsl(var(--primary))]/60"
      />
      <select
        value={machineNo} onChange={(e) => setMachineNo(e.target.value)}
        data-testid="mt-spares-machine"
        className="h-8 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-2 text-[12.5px] outline-none focus:border-[hsl(var(--primary))]/60"
      >
        <option value="">No machine</option>
        {(machines || []).map((m) => <option key={m.machine_no} value={m.machine_no}>{m.name} · {m.machine_no}</option>)}
      </select>
      <button
        onClick={submit} disabled={!part.trim() || busy} data-testid="mt-spares-save"
        title={part.trim() ? undefined : "Name the part first"}
        className="rl-lit-btn h-8 px-3.5 rounded-full bg-[hsl(var(--primary))] text-white text-[12px] font-bold disabled:opacity-45 disabled:cursor-not-allowed"
      >
        {busy ? "Saving…" : "Log order"}
      </button>
      <button onClick={() => setOpen(false)} className="rl-pill h-8 px-3.5 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[12px] font-semibold">
        Cancel
      </button>
    </div>
  );
}

export default function MaintenanceSparesPage() {
  const navigate = useNavigate();
  const { can = {}, refresh, refreshKey } = useMaintenance() || {};
  const [data, setData] = useState(null);
  const [machines, setMachines] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    const t = setInterval(() => setNowMs(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/maintenance/spares`);
      setData(r.data || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load spare orders.");
      setData({ spares: [], pending: 0 });
    }
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => {
    axios.get(`${API}/maintenance/machines`).then((r) => setMachines(r.data?.machines || [])).catch(() => {});
  }, []);

  const move = useCallback(async (sp, status, msg) => {
    setBusyId(sp.id);
    try {
      await axios.post(`${API}/maintenance/spares/${sp.id}/status`, { status });
      toast.success(msg);
      await load();
      refresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "That didn't go through.");
    } finally { setBusyId(null); }
  }, [load, refresh]);

  const rows = data?.spares || null;

  return (
    <div className="rl-glass-edge rounded-[14px] bg-[hsl(var(--card))] overflow-hidden" data-testid="mt-spares-page">
      <div className="px-[18px] pt-4 pb-3 flex items-start justify-between gap-3 flex-wrap border-b border-[hsl(var(--border))]">
        <div>
          <div className="text-[17px] font-extrabold flex items-center gap-2.5" style={{ fontFamily: "var(--rl-font-heading), sans-serif" }}>
            <Package className="w-[18px] h-[18px] text-[#2F6DAE]" />
            Spare Orders
          </div>
          <div className="text-[11.5px] text-[hsl(var(--muted-foreground))] mt-[3px]">
            What's on order, what it cost, and which breakdown it unblocks
          </div>
        </div>
        {can.can_repair_machines && <AddRow machines={machines} onAdded={() => { load(); refresh?.(); }} />}
      </div>

      <div className="px-[18px] py-3 flex items-center gap-7 flex-wrap border-b border-[hsl(var(--border))]">
        <div>
          <div className="text-[10px] font-medium uppercase tracking-[0.08em] text-[hsl(var(--muted-foreground))]/70 mb-[5px]">Pending</div>
          <div className="font-mono-data font-bold text-[1.25rem] leading-none tracking-[-0.025em]">{data?.pending ?? "—"}</div>
        </div>
        <div className="w-px h-8 bg-[hsl(var(--border))]/70" aria-hidden="true" />
        <div>
          <div className="text-[10px] font-medium uppercase tracking-[0.08em] text-[hsl(var(--muted-foreground))]/70 mb-[5px]">Value on order</div>
          <div
            className={`font-mono-data font-bold text-[1.25rem] leading-none tracking-[-0.025em] ${data?.pending_value_inr == null ? "text-[hsl(var(--muted-foreground))]" : ""}`}
            title={data?.pending_value_inr == null ? "No prices entered on the open orders" : undefined}
          >
            {data?.pending_value_inr == null ? "₹ —" : formatINR(data.pending_value_inr)}
          </div>
        </div>
      </div>

      <table className="w-full border-collapse">
        <thead className="rl-thead">
          <tr>
            <Th width="90px">Order</Th>
            <Th>Part</Th>
            <Th right width="70px">Qty</Th>
            <Th right width="110px">₹</Th>
            <Th width="22%">Machine</Th>
            <Th>Breakdown</Th>
            <Th>Ordered</Th>
            <Th right>Status</Th>
          </tr>
        </thead>
        <tbody>
          {rows === null && (
            <tr><td colSpan={8} className="px-4 py-10 text-center text-[12px] text-[hsl(var(--muted-foreground))]">Loading…</td></tr>
          )}
          {rows && rows.length === 0 && (
            <tr>
              <td colSpan={8} className="px-4 py-12 text-center">
                <p className="text-[13px] text-[hsl(var(--muted-foreground))] max-w-[460px] mx-auto leading-relaxed">
                  Nothing on order. Pause a repair for parts and log the order in the same step — it stays here until it arrives.
                </p>
              </td>
            </tr>
          )}
          {(rows || []).map((sp) => {
            const busy = busyId === sp.id;
            const done = sp.status === "received";
            return (
              <tr key={sp.id} className="hover:bg-[hsl(var(--muted))]/25" data-testid={`mt-spare-row-${sp.id}`}>
                <Td><span className="font-mono-data text-[12px] font-bold">{sp.id}</span></Td>
                <Td>
                  <div className="text-[13px] font-[650] leading-[1.35]">{sp.part_name}</div>
                  {sp.note && <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-[3px]">{sp.note}</div>}
                </Td>
                <Td right><span className="font-mono-data text-[12.5px]">{Number(sp.qty) % 1 === 0 ? Number(sp.qty) : sp.qty}</span></Td>
                <Td right>
                  <span className={`font-mono-data text-[12.5px] font-bold ${sp.price_inr == null ? "text-[hsl(var(--muted-foreground))] font-normal" : ""}`}>
                    {sp.price_inr == null ? "—" : formatINR(sp.price_inr)}
                  </span>
                </Td>
                <Td>{sp.machine ? <MachineIdentity machine={sp.machine} size={24} showCategory={false} /> : <span className="text-[12px] text-[hsl(var(--muted-foreground))]">—</span>}</Td>
                <Td>
                  {sp.ticket_id ? (
                    <button
                      onClick={() => navigate("/maintenance/under-repair")}
                      title="Go to the breakdown this order unblocks"
                      data-testid={`mt-spare-ticket-${sp.id}`}
                      className="rl-me-chip rl-carved font-mono-data hover:text-[hsl(var(--foreground))]"
                    >
                      {sp.ticket_id}
                    </button>
                  ) : <span className="text-[12px] text-[hsl(var(--muted-foreground))]">—</span>}
                </Td>
                <Td>
                  <div className="text-[11.5px]">by <b className="font-[650]">{sp.ordered_by?.name || "—"}</b></div>
                  <div className="text-[10.5px] text-[hsl(var(--muted-foreground))]">{agoFrom(sp.ordered_at, nowMs)}</div>
                  {done && sp.received_at && (
                    <div className="text-[10.5px] rl-tx-green mt-[3px]">received {agoFrom(sp.received_at, nowMs)}</div>
                  )}
                </Td>
                <Td right>
                  <div className="flex items-center justify-end gap-2 flex-wrap">
                    <StatusChip status={sp.status} />
                    {can.can_repair_machines && !done && (
                      <>
                        {sp.status === "ordered" && (
                          <button
                            onClick={() => move(sp, "backordered", `${sp.part_name} marked backordered`)}
                            disabled={busy}
                            data-testid={`mt-spare-backorder-${sp.id}`}
                            className="rl-pill inline-flex items-center gap-1.5 h-7 px-3 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[11.5px] font-semibold disabled:opacity-50"
                          >
                            <Clock className="w-3 h-3" /> Backordered
                          </button>
                        )}
                        <button
                          onClick={() => move(sp, "received", `${sp.part_name} received`)}
                          disabled={busy}
                          data-testid={`mt-spare-receive-${sp.id}`}
                          className="rl-pill inline-flex items-center gap-1.5 h-7 px-3 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[11.5px] font-semibold disabled:opacity-50"
                        >
                          <CheckCircle2 className="w-3 h-3" /> Mark received
                        </button>
                      </>
                    )}
                    {can.can_repair_machines && done && (
                      <span className="text-[10.5px] text-[hsl(var(--muted-foreground))] italic" title="A received order is the end of the line — nothing is deleted">
                        closed
                      </span>
                    )}
                    {!can.can_repair_machines && (
                      <span className="text-[10.5px] text-[hsl(var(--muted-foreground))] italic" title="Only the maintenance team updates spare orders">
                        maintenance updates these
                      </span>
                    )}
                  </div>
                </Td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
